// Client messaging (PRD 68) — the firm's side.
//
// THERE IS NO NOTES CALL HERE, and there will not be one on this service:
// internal notes get their own endpoint and their own client in Wave 4,
// because "two verbs on one controller that differ by a parameter is how the
// wrong one gets called" (§4.3) — and the same is true of two methods on one
// service object.

import {
  apiDelete, apiGet, apiPost, apiPostForm, apiPut, businessApiGet, businessApiPost,
  businessApiPostForm,
} from '../utils/apiUtils';
import { parseData, parseEnvelope } from './businessApiCommon';
import {
  AttachmentRules, ClientMessageThread, HeldInboundMessage, MessageThread, ThreadContextRef,
  ThreadNote,
} from '../types/messagingApi';

const firmHeaders = (firmId: string) => ({ headers: { 'X-Firm-Id': firmId } });

/**
 * MULTIPART ONLY WHEN THERE ARE FILES. A message with none goes as the JSON it
 * always went as, so nothing about the existing path changes.
 */
function messageForm(fields: Record<string, string | undefined>, files: File[]): FormData {
  const form = new FormData();
  Object.entries(fields).forEach(([key, value]) => {
    if (value !== undefined) form.append(key, value);
  });
  files.forEach(file => form.append('attachments[]', file, file.name));
  return form;
}

/** `meta.attachments` from an inbox response, or null from an api without it. */
async function rulesFrom(response: Response): Promise<AttachmentRules | null> {
  const { meta } = await parseEnvelope<unknown>(response);
  const rules = (meta as { attachments?: AttachmentRules }).attachments;
  return rules && typeof rules.max_files === 'number' ? rules : null;
}

/** The bytes of one file. Throws with the server's message on a refusal. */
async function bytesOf(response: Response): Promise<Blob> {
  if (!response.ok) {
    await parseData(response);
    throw new Error('Could not download that file');
  }
  return response.blob();
}

const inlineQuery = (inline?: boolean) => (inline ? '?disposition=inline' : '');

export const messagingService = {
  /** `filter=awaiting_us` comes back OLDEST FIRST — longest wait on top. */
  async threads(firmId: string, params: { filter?: string; client?: string } = {}) {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => value && query.set(key, value));
    const suffix = query.toString() ? `?${query.toString()}` : '';
    return parseData<MessageThread[]>(
      await apiGet(`/api/v1/firm/messages${suffix}`, firmHeaders(firmId)),
    );
  },

  async thread(firmId: string, id: string): Promise<MessageThread> {
    return parseData<MessageThread>(
      await apiGet(`/api/v1/firm/messages/${id}`, firmHeaders(firmId)),
    );
  },

  async start(firmId: string, input: {
    firm_client_id: string; subject: string; body: string; business_id?: string;
    context?: ThreadContextRef;
  }): Promise<MessageThread> {
    return parseData<MessageThread>(
      await apiPost('/api/v1/firm/messages', input, firmHeaders(firmId)),
    );
  },

  async reply(firmId: string, id: string, body: string, files: File[] = []): Promise<MessageThread> {
    const path = `/api/v1/firm/messages/${id}/messages`;
    return parseData<MessageThread>(
      files.length
        ? await apiPostForm(path, messageForm({ body }, files), firmHeaders(firmId))
        : await apiPost(path, { body }, firmHeaders(firmId)),
    );
  },

  /** Null until the api takes files — and then nothing offers to attach one. */
  async attachmentRules(firmId: string): Promise<AttachmentRules | null> {
    return rulesFrom(await apiGet('/api/v1/firm/messages', firmHeaders(firmId)));
  },

  /** Either side's file on a MESSAGE. A note's file has its own call below. */
  async attachment(firmId: string, threadId: string, attachmentId: number,
                   options: { inline?: boolean } = {}): Promise<Blob> {
    return bytesOf(await apiGet(
      `/api/v1/firm/messages/${threadId}/attachments/${attachmentId}${inlineQuery(options.inline)}`,
      firmHeaders(firmId),
    ));
  },

  /**
   * §4.4 — a thread may be assigned to a membership; `null` unassigns, which
   * is a first-class value ("unassigned is normal and fine"). The server
   * refuses somebody who is not already in the thread.
   */
  async assign(firmId: string, id: string, firmMembershipId: string | null) {
    return parseData<MessageThread>(
      await apiPost(`/api/v1/firm/messages/${id}/assign`,
                    { firm_membership_id: firmMembershipId }, firmHeaders(firmId)),
    );
  },

  /**
   * Internal notes (PRD 68 §4.3). A SEPARATE ENDPOINT, not a parameter on
   * `reply` — "two verbs on one controller that differ by a parameter is how
   * the wrong one gets called", and the same is true of two methods here.
   */
  async notes(firmId: string, threadId: string): Promise<ThreadNote[]> {
    return parseData<ThreadNote[]>(
      await apiGet(`/api/v1/firm/messages/${threadId}/notes`, firmHeaders(firmId)),
    );
  },

  async addNote(firmId: string, threadId: string, body: string,
                files: File[] = []): Promise<ThreadNote> {
    const path = `/api/v1/firm/messages/${threadId}/notes`;
    return parseData<ThreadNote>(
      files.length
        ? await apiPostForm(path, messageForm({ body }, files), firmHeaders(firmId))
        : await apiPost(path, { body }, firmHeaders(firmId)),
    );
  },

  /** A NOTE'S file, through the notes route — the only route that serves one. */
  async noteAttachment(firmId: string, threadId: string, noteId: string, attachmentId: number,
                       options: { inline?: boolean } = {}): Promise<Blob> {
    return bytesOf(await apiGet(
      `/api/v1/firm/messages/${threadId}/notes/${noteId}/attachments/${attachmentId}` +
        inlineQuery(options.inline),
      firmHeaders(firmId),
    ));
  },

  /** Withdraws a note. Redacted, never deleted, and only by its author. */
  async withdrawNote(firmId: string, threadId: string, noteId: string): Promise<ThreadNote> {
    return parseData<ThreadNote>(
      await apiDelete(`/api/v1/firm/messages/${threadId}/notes/${noteId}`, firmHeaders(firmId)),
    );
  },

  /** §4.6 — mute is per thread AND PER PARTICIPANT: this silences nobody else. */
  async mute(firmId: string, id: string, muted: boolean): Promise<MessageThread> {
    return parseData<MessageThread>(
      await apiPost(`/api/v1/firm/messages/${id}/${muted ? 'mute' : 'unmute'}`, {},
                    firmHeaders(firmId)),
    );
  },

  /**
   * Emailed replies waiting on somebody to look (PRD 68 §4.7). Pending only —
   * what has been dealt with is not a queue.
   */
  async heldMessages(firmId: string): Promise<HeldInboundMessage[]> {
    return parseData<HeldInboundMessage[]>(
      await apiGet('/api/v1/firm/messages/held', firmHeaders(firmId)),
    );
  },

  /** Accepts it onto the thread, as the participant whose address it came to. */
  async postHeld(firmId: string, id: string): Promise<HeldInboundMessage> {
    return parseData<HeldInboundMessage>(
      await apiPost(`/api/v1/firm/messages/held/${id}/post`, {}, firmHeaders(firmId)),
    );
  },

  /** Resolved, not deleted — the row stays so the question stays answerable. */
  async discardHeld(firmId: string, id: string): Promise<HeldInboundMessage> {
    return parseData<HeldInboundMessage>(
      await apiPost(`/api/v1/firm/messages/held/${id}/discard`, {}, firmHeaders(firmId)),
    );
  },

  /** Whole-set replace of the firm side; the server checks every id. */
  async setParticipants(firmId: string, id: string, firmMembershipIds: string[]) {
    return parseData<MessageThread>(
      await apiPut(`/api/v1/firm/messages/${id}/participants`,
                   { firm_membership_ids: firmMembershipIds }, firmHeaders(firmId)),
    );
  },

};

/**
 * The client's own side (PRD 68 §5.2). A SEPARATE OBJECT from the firm's, not
 * more methods on it: the two read different shapes, and the client's must
 * never gain a way to reach an internal note (§4.3). Business-scoped, so it
 * rides the tenant header every other business call uses.
 */
export const clientMessagingService = {
  async threads(): Promise<ClientMessageThread[]> {
    return parseData<ClientMessageThread[]>(await businessApiGet('/api/v1/business/messages'));
  },

  async thread(id: string): Promise<ClientMessageThread> {
    return parseData<ClientMessageThread>(
      await businessApiGet(`/api/v1/business/messages/${id}`),
    );
  },

  /** §4.9 — "Ask your accountant" is this, with a `context` attached. */
  async start(input: {
    subject: string; body: string; context?: ThreadContextRef;
  }, files: File[] = []): Promise<ClientMessageThread> {
    const path = '/api/v1/business/messages';
    if (!files.length) return parseData<ClientMessageThread>(await businessApiPost(path, input));

    const form = messageForm({ subject: input.subject, body: input.body }, files);
    if (input.context) {
      form.append('context[type]', input.context.type);
      form.append('context[id]', input.context.id);
    }
    return parseData<ClientMessageThread>(await businessApiPostForm(path, form));
  },

  async reply(id: string, body: string, files: File[] = []): Promise<ClientMessageThread> {
    const path = `/api/v1/business/messages/${id}/messages`;
    return parseData<ClientMessageThread>(
      files.length
        ? await businessApiPostForm(path, messageForm({ body }, files))
        : await businessApiPost(path, { body }),
    );
  },

  /** Null until the api takes files — and then nothing offers to attach one. */
  async attachmentRules(): Promise<AttachmentRules | null> {
    return rulesFrom(await businessApiGet('/api/v1/business/messages'));
  },

  async attachment(threadId: string, attachmentId: number,
                   options: { inline?: boolean } = {}): Promise<Blob> {
    return bytesOf(await businessApiGet(
      `/api/v1/business/messages/${threadId}/attachments/${attachmentId}${inlineQuery(options.inline)}`,
    ));
  },

  /** Opening a thread is reading it — the firm's "Seen" is built on this. */
  async markRead(id: string): Promise<ClientMessageThread> {
    return parseData<ClientMessageThread>(
      await businessApiPost(`/api/v1/business/messages/${id}/read`),
    );
  },

  async setMuted(id: string, muted: boolean): Promise<ClientMessageThread> {
    return parseData<ClientMessageThread>(
      await businessApiPost(`/api/v1/business/messages/${id}/${muted ? 'mute' : 'unmute'}`),
    );
  },
};
